import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Text, Button, Image, Skeleton, Stack, useDisclosure } from '@chakra-ui/react'
import { AlertComponent, AlertDescription, AlertTitle } from '@wow/alert'

import FooterButtons from 'components/common/FooterButtons/FooterButtons'
import BankAccordion from 'components/offering/BankAcordation/BankAcordation'
import SummaryPlan from 'components/offering/Summary/SummaryPlan'
import IBalert from 'components/common/IBalert/IBalert'
import { useGlobalContext } from 'providers/GlobalProvider'
import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import { getselectedBanks, postAccounts } from 'services/api'

import { IBanks, IFetchedGetSelectedBanks, IResponseDataAccount } from 'services/IApi.d'
import { IAccountProvider } from 'providers/offeringProvider/IOfferingProvider.d'
import UrgentPriority from 'assets/images/urgent-priority.svg'

const Accounts = () => {
  const navigate = useNavigate()

  const { idEmpresa, idSuscriptor } = useGlobalContext()
  const { accounts: bankListCopy, selectedOfferings: { plan: { codeDn } }, setSelectedOfferings, selectedAccounts, accountError, noCuitError, sendDataLayer } = useOfferingProvider()
  const [bankList, setBankList] = useState<IAccountProvider[]>([])
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)
  const [loadingNext, setLoadingNext] = useState<boolean>(false)
  const [loadingPrevious, setLoadingPrevious] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [lastSelectAccount, setLastSelectAccount] = useState({ indexParent: 0, indexChild: 0 })
  const [isPrimaryAccountUSD, setIsPrimaryAccountUSD] = useState<boolean>(false)
  const [showErrorLayer, setShowErrorLayer] = useState(false)
  const { isOpen: isOpenError, onOpen: onOpenError, onClose: onCloseError } = useDisclosure()

  const handleBack = () => navigate('/planes')
  const goToDashboard = () => navigate('/dashboard')

  const title = 'Elegí bancos y cuentas'
  const titleSub = 'Seleccioná aquellos con los que querés operar. Algunos bancos pueden requerir suscripción física.'
  const subtitle = 'Obtuvimos tus bancos y cuentas a través del CUIT de tu compañía'

  const alertErrorPostRegister = {
    title: 'No podemos guardar tu selección',
    text: 'Estamos trabajando para solucionarlo, volvé a intentar más tarde.',
    type: 'error',
  }

  const errorLayer = {
    title: 'No encontramos cuentas habilitadas para tu CUIT',
    firstText: 'Las cuentas que obtenemos son informadas por los bancos.',
    secondText: 'Si las diste de alta recientemente esperá 24 horas. Si no, comunicate con tu ejecutivo de cuentas del banco.',
    textButton: 'Volver al inicio',
  }

  const getSelectedAccounts = (accountsArray: IAccountProvider[]): IBanks[] => (
    accountsArray?.filter(bank =>
      bank.accounts
        .some(account => account.checked))
      .map(banks => ({
        ...banks,
        accounts: (banks.accounts.filter(account => account.checked))
          .map(account => ({
            accountId: account.accountId,
            cbu: account.cbu,
            currency: account.currency,
            type: account.type,
            codeDn,
          }))
      })
      )
  )

  const handleContinue = async () => {
    setLoadingNext(true)
    setIsButtonDisabled(true)

    const accounts: IAccountProvider[] = structuredClone(selectedAccounts)
    const onlySelectedAccounts = getSelectedAccounts(accounts)
    const response = await postAccounts({
      subscriberId: idSuscriptor,
      companyId: idEmpresa,
      banks: onlySelectedAccounts,
    })

    if ((response?.status === 201 || response?.status === 200)) {
      sendDataLayer({
        event: 'completed_onboarding_step',
        onboarding_step: 'Cargar bancos y cuentas',
        first_time: 'Si',
      })
      setLoadingNext(false)
      setSelectedOfferings(old => ({ ...old, accounts: structuredClone(onlySelectedAccounts) }))
    } else {
      onOpenError()
      setIsButtonDisabled(false)
      setLoadingNext(false)
      return
    }
    navigate('/servicios-adicionales')
  }

  const getPreviousSelectedAccounts = async () => {
    try {
      const response: IFetchedGetSelectedBanks = await getselectedBanks(idSuscriptor, idEmpresa)
      if (response?.status === 200) {
        const previousSelectedAccounts = response?.data?.banks
        const updatedBankList: IResponseDataAccount[] = bankListCopy.map(bank => {
          const matchedSelectedAccount = previousSelectedAccounts.find(selectedBank => selectedBank.bankId === bank.bankId)
          if (matchedSelectedAccount) {
            const updatedAccounts = bank.accounts.map(account => {
              const isSelected: boolean = matchedSelectedAccount.accounts.some(selectedAccount => selectedAccount.accountId === account.accountId)
              return {
                ...account,
                checked: isSelected,
              }
            })
            return {
              ...bank,
              accounts: updatedAccounts,
            }
          }
          return bank
        })
        setBankList(updatedBankList)
        setSelectedOfferings(old => ({ ...old, accounts: updatedBankList }))
        bankListCopy && setLoadingPrevious(false)
      } else if (response?.status === 204) {
        bankListCopy && setLoadingPrevious(false)
        setBankList(bankListCopy)
      } else {
        navigate('/error')
      }
    } catch (error) {
      // Handle error
    }
  }
  // buscar los bancos/cuentas guardadas 
  useEffect(() => {
    getPreviousSelectedAccounts()
  }, [bankListCopy])
  

  const validateSelectedAccounts = (selectedAccounts: IAccountProvider[]): boolean => {
    let hasSelectedPesos = false;
    const banksWithSelectedDollars = new Set<number>();
  
    selectedAccounts.forEach(bank => {
      let hasPesos = false;
      let hasDollars = false;
  
      bank.accounts?.forEach(account => {
        if (account.checked) {
          if (account.currency === "$") hasPesos = true;
          if (account.currency === "USD") hasDollars = true;
        }
      });
  
      if (hasPesos) hasSelectedPesos = true;
      if (hasDollars && !hasPesos) banksWithSelectedDollars.add(bank.bankId);
    });
  
    return hasSelectedPesos && banksWithSelectedDollars.size === 0;
  };
  
  useEffect(() => {
    setIsButtonDisabled(!validateSelectedAccounts(selectedAccounts));
  }, [selectedAccounts]);

  useEffect(() => {
    setIsPrimaryAccountUSD(bankList.some(item => item.accounts?.some(account => account.currency === 'USD')))
  }, [bankList])

  useEffect(() => {
  if (noCuitError || !bankListCopy.length ) {
    setShowErrorLayer(true)
  } else if (accountError) {
    navigate('/error')
  }
  }, [noCuitError, accountError, bankListCopy])


  return (
    <div>
      <Box>
        <Box textStyle='h1' mb={2}>{title}</Box>
        <Box textStyle='bodyLg' mb={7}>{titleSub}</Box>
       
        
        {showErrorLayer ? (
          <Flex h='calc(40vh - 66px)' flexDir='column' align='center' justifyContent='center'>
            <Image alt='Error image' src={UrgentPriority} />
            <Text textStyle='h4' p={2}>{errorLayer.title}</Text>
            <Text textStyle='bodyLg'>{errorLayer.firstText}</Text>
            <Text textStyle='bodyLg' pb={8}>{errorLayer.secondText}</Text>
            <Button data-testid='actionButton' variant='primary-text' onClick={goToDashboard}>
              <Text textStyle='labelLgBold'>{errorLayer.textButton}</Text>
            </Button>
          </Flex>
        ) : (
          <>
          <Box textStyle='h5' mb={5}>{subtitle}</Box>
            <Flex>
              <Box flex={0.66} height='45vh' overflow='auto' pr={4} pb={4}>
                {isPrimaryAccountUSD &&
                  <Box mb='4' >
                    <AlertComponent
                      id="alert-id"
                      variant="warning"
                    >
                      <AlertTitle>Información para cuentas en dólares</AlertTitle>
                      <AlertDescription>
                          Si necesitás operar con una cuenta en dólares (u$s), elegí por lo menos una cuenta en pesos ($) del mismo banco.
                      </AlertDescription>
                      <AlertDescription>
                          No podemos agregar bancos que solo tengan seleccionadas cuentas en dólares.
                      </AlertDescription>
                    </AlertComponent>
                  </Box> 
                }
                {loadingPrevious && (
                  <Stack>
                    <Skeleton height='100px' />
                    <Skeleton height='100px' />
                    <Skeleton height='100px' />
                  </Stack>
                )}
                {!loadingPrevious &&
                  bankList?.map(bank => (
                    <BankAccordion
                      isPhysicalSubscription={!bank.acceptDigitalSubscription}
                      name={bank.name}
                      accountsBank={bank?.accounts}
                      key={bank.bankId}
                      bankId={bank.bankId}
                      viewPage='accounts'
                      setLastSelectAccount={setLastSelectAccount}
                    />
                  ))}
              </Box>
              <Box flex={0.34}>
                <SummaryPlan step='accounts' />
              </Box>
            </Flex>
            <FooterButtons
              onBack={handleBack}
              onNext={handleContinue}
              backDisabled={false}
              nextDisabled={isButtonDisabled}
              loading={loadingNext}
              backText='Volver'
              nextText='Continuar'
            />
          </>
        )}
        <IBalert
          isOpen={isOpenError}
          onClose={onCloseError}
          title={alertErrorPostRegister.title}
          text={alertErrorPostRegister.text}
          buttonClose
          type={alertErrorPostRegister.type}
        />
      </Box>
    </div>
  )
}

export default Accounts
